<template>
  <div class="help-center-page">
    <div class="container">
      <div class="help-header">
        <h1>Help Center</h1>
        <p>How can we help you today?</p>
      </div>

      <!-- Order Tracking Section -->
      <div class="help-section tracking-section">
        <div class="section-icon">
          <i class="fas fa-shipping-fast"></i>
        </div>
        <h2>Track Your Order</h2>
        <p>Enter your order number and email to track your order status</p>
        
        <form @submit.prevent="trackOrder" class="tracking-form">
          <div class="form-row">
            <div class="form-group">
              <label>Order Number *</label>
              <input 
                v-model="trackingForm.order_number" 
                type="text" 
                placeholder="e.g. ORD-123456"
                required
                :disabled="tracking"
              >
            </div>
            <div class="form-group">
              <label>Email Address *</label>
              <input 
                v-model="trackingForm.email" 
                type="email" 
                placeholder="Email used for order"
                required
                :disabled="tracking"
              >
            </div>
          </div>
          <button type="submit" class="btn-track" :disabled="tracking">
            <i v-if="tracking" class="fas fa-spinner fa-spin"></i>
            <i v-else class="fas fa-search"></i>
            {{ tracking ? 'Searching...' : 'Track Order' }}
          </button>
        </form>

        <!-- Tracking Result -->
        <div v-if="trackingResult" class="tracking-result">
          <div class="result-header">
            <h3>Order #{{ trackingResult.number }}</h3>
            <span class="status-badge" :class="getStatusClass(trackingResult.status)">
              {{ formatStatus(trackingResult.status) }}
            </span>
          </div>
          
          <div class="result-details">
            <div class="detail-item">
              <i class="fas fa-calendar-alt"></i>
              <div>
                <span class="label">Order Date</span>
                <span class="value">{{ formatDate(trackingResult.created_at) }}</span>
              </div>
            </div>
            
            <div v-if="trackingResult.shipped_at" class="detail-item">
              <i class="fas fa-truck"></i>
              <div>
                <span class="label">Shipped Date</span>
                <span class="value">{{ formatDate(trackingResult.shipped_at) }}</span>
              </div>
            </div>
            
            <div v-if="trackingResult.tracking_number" class="detail-item">
              <i class="fas fa-barcode"></i>
              <div>
                <span class="label">Tracking Number</span>
                <span class="value tracking-number">{{ trackingResult.tracking_number }}</span>
              </div>
            </div>
            
            <div v-if="trackingResult.shipping_carrier" class="detail-item">
              <i class="fas fa-building"></i>
              <div>
                <span class="label">Carrier</span>
                <span class="value">{{ trackingResult.shipping_carrier }}</span>
              </div>
            </div>
            
            <div v-if="trackingResult.estimated_delivery_date" class="detail-item">
              <i class="fas fa-clock"></i>
              <div>
                <span class="label">Estimated Delivery</span>
                <span class="value">{{ formatDate(trackingResult.estimated_delivery_date) }}</span>
              </div>
            </div>
            
            <div v-if="trackingResult.delivered_at" class="detail-item success">
              <i class="fas fa-check-circle"></i>
              <div>
                <span class="label">Delivered</span>
                <span class="value">{{ formatDate(trackingResult.delivered_at) }}</span>
              </div>
            </div>

            <div class="detail-item">
              <i class="fas fa-map-marker-alt"></i>
              <div>
                <span class="label">Shipping To</span>
                <span class="value">{{ trackingResult.shipping_city }}, {{ trackingResult.shipping_state }}</span>
              </div>
            </div>
          </div>

          <!-- Order Status Timeline -->
          <div v-if="trackingResult.status !== 'cancelled'" class="status-timeline">
            <div class="timeline-item" :class="{ active: isStatusReached('pending') }">
              <div class="timeline-dot"></div>
              <span>Order Placed</span>
            </div>
            <div class="timeline-item" :class="{ active: isStatusReached('paid') }">
              <div class="timeline-dot"></div>
              <span>Processing</span>
            </div>
            <div class="timeline-item" :class="{ active: isStatusReached('shipped') }">
              <div class="timeline-dot"></div>
              <span>Shipped</span>
            </div>
            <div class="timeline-item" :class="{ active: isStatusReached('delivered') }">
              <div class="timeline-dot"></div>
              <span>Delivered</span>
            </div>
          </div>
          
          <!-- Cancelled Status -->
          <div v-else class="status-timeline cancelled">
            <div class="timeline-item active cancelled">
              <div class="timeline-dot"></div>
              <span>Order Cancelled</span>
            </div>
          </div>
        </div>

        <div v-if="trackingError" class="tracking-error">
          <i class="fas fa-exclamation-circle"></i>
          <p>{{ trackingError }}</p>
        </div>
      </div>

      <!-- Quick Links Section -->
      <div class="help-grid">
        <a href="/contact" class="help-card">
          <div class="card-icon">
            <i class="fas fa-envelope"></i>
          </div>
          <h3>Contact Us</h3>
          <p>Get in touch with our support team</p>
        </a>

        <a href="/policies" class="help-card">
          <div class="card-icon">
            <i class="fas fa-shield-alt"></i>
          </div>
          <h3>Privacy Policy</h3>
          <p>How we protect your information</p>
        </a>

        <a href="/terms" class="help-card">
          <div class="card-icon">
            <i class="fas fa-file-contract"></i>
          </div>
          <h3>Terms of Service</h3>
          <p>Our terms and conditions</p>
        </a>

        <a href="/refund" class="help-card">
          <div class="card-icon">
            <i class="fas fa-undo-alt"></i>
          </div>
          <h3>Returns & Refunds</h3>
          <p>Our return and refund policy</p>
        </a>
      </div>

      <!-- FAQ Section -->
      <div class="help-section faq-section">
        <h2>Frequently Asked Questions</h2>
        
        <div class="faq-list">
          <div class="faq-item" v-for="(faq, index) in faqs" :key="index">
            <button class="faq-question" @click="toggleFaq(index)">
              <span>{{ faq.question }}</span>
              <i class="fas" :class="openFaq === index ? 'fa-chevron-up' : 'fa-chevron-down'"></i>
            </button>
            <div class="faq-answer" v-show="openFaq === index">
              <p>{{ faq.answer }}</p>
            </div>
          </div>
        </div>
      </div>

      <!-- Contact Info -->
      <div class="contact-info">
        <div class="info-item">
          <i class="fas fa-phone"></i>
          <div>
            <span class="label">Phone</span>
            <span class="value">07011110034</span>
          </div>
        </div>
        <div class="info-item">
          <i class="fas fa-envelope"></i>
          <div>
            <span class="label">Email</span>
            <span class="value">public@ORLEY COSMETICS.com</span>
          </div>
        </div>
        <div class="info-item">
          <i class="fas fa-clock"></i>
          <div>
            <span class="label">Hours</span>
            <span class="value">Mon - Sat: 9am - 6pm</span>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'HelpCenter',
  data() {
    return {
      trackingForm: {
        order_number: '',
        email: ''
      },
      tracking: false,
      trackingResult: null,
      trackingError: '',
      openFaq: null,
      faqs: [
        {
          question: 'How long does delivery take?',
          answer: 'Delivery typically takes 2-5 business days within Lagos and 5-10 business days for other states. You can track your order using the tracking form above.'
        },
        {
          question: 'What payment methods do you accept?',
          answer: 'We accept payments via Paystack and Flutterwave, which support credit/debit cards, bank transfers, and USSD payments.'
        },
        {
          question: 'How do I return an item?',
          answer: 'To return an item, please contact our support team within 7 days of delivery. The item must be unused and in its original packaging.'
        },
        {
          question: 'Can I cancel my order?',
          answer: 'Orders can be cancelled before they are shipped. Once shipped, you will need to wait for delivery and then initiate a return.'
        },
        {
          question: 'How do I track my order?',
          answer: 'Use the tracking form at the top of this page. Enter your order number and the email address used during checkout.'
        }
      ]
    }
  },
  methods: {
    async trackOrder() {
      this.tracking = true
      this.trackingResult = null
      this.trackingError = ''

      try {
        const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content

        const response = await fetch('/api/track-order', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-CSRF-TOKEN': csrfToken,
            'Accept': 'application/json'
          },
          body: JSON.stringify(this.trackingForm)
        })

        const data = await response.json()

        if (response.ok && data.success) {
          this.trackingResult = data.order
        } else {
          this.trackingError = data.message || 'Order not found. Please check your order number and email.'
        }
      } catch (error) {
        this.trackingError = 'An error occurred. Please try again.'
      } finally {
        this.tracking = false
      }
    },

    formatStatus(status) {
      const statusMap = {
        'pending': 'Order Placed',
        'paid': 'Processing',
        'processing': 'Processing',
        'shipped': 'Shipped',
        'delivered': 'Delivered',
        'cancelled': 'Cancelled',
        'refunded': 'Refunded'
      }
      return statusMap[status] || status
    },

    getStatusClass(status) {
      const classMap = {
        'pending': 'status-pending',
        'paid': 'status-processing',
        'processing': 'status-processing',
        'shipped': 'status-shipped',
        'delivered': 'status-delivered',
        'cancelled': 'status-cancelled',
        'refunded': 'status-refunded'
      }
      return classMap[status] || ''
    },

    isStatusReached(status) {
      if (!this.trackingResult) return false
      
      const currentStatus = this.trackingResult.status
      const statusOrder = ['pending', 'paid', 'shipped', 'delivered']
      const currentIndex = statusOrder.indexOf(currentStatus)
      const checkIndex = statusOrder.indexOf(status)
      
      // Handle processing status as alias for paid
      if (currentStatus === 'processing') {
        const processingIndex = statusOrder.indexOf('paid')
        return checkIndex <= processingIndex
      }
      
      return checkIndex <= currentIndex && currentIndex !== -1
    },

    formatDate(dateString) {
      if (!dateString) return ''
      const date = new Date(dateString)
      return date.toLocaleDateString('en-NG', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      })
    },

    toggleFaq(index) {
      this.openFaq = this.openFaq === index ? null : index
    }
  }
}
</script>
